import { phoneMask } from '@shared/constants/phone-mask';
import { COUNTRY_TIMEZONES } from '@shared/constants/timezones';

export interface ParsedPhoneNumber {
  country: string | null;
  iso2: string | null;
  dialCode: string | null;
  areaCode?: string | null;
  nationalNumber: string;
  formattedNumber: string;
}

type CountryMeta = {
  name: string;
  iso2: string;
  dialCode?: string;
  format?: string;
  areaCodes?: string[];
};

export class PhoneNumberParser {
  private readonly countries: CountryMeta[];

  constructor() {
    this.countries = Object.entries(COUNTRY_TIMEZONES).map(([iso2, data]) => ({
      name: iso2,
      iso2,
      dialCode: Array.isArray(data.callingCode) ? data.callingCode[0] : data.callingCode,
      format: phoneMask[iso2 as keyof typeof phoneMask],
      areaCodes: Array.isArray(data.callingCode) ? data.callingCode : undefined,
    }));
  }

  public parse(phone?: string | null): ParsedPhoneNumber {
    if (!phone) {
      return this.emptyResult();
    }

    const digits = phone.replace(/\D/g, '');

    if (!digits) {
      return this.emptyResult();
    }

    const sortedCountries = [...this.countries].sort((a, b) => (b.dialCode?.length || 0) - (a.dialCode?.length || 0));

    for (const country of sortedCountries) {
      if (!country.dialCode) continue;

      if (digits.startsWith(country.dialCode)) {
        let nationalNumber = digits.slice(country.dialCode.length);
        let areaCode: string | undefined;

        if (country.areaCodes?.length) {
          for (const code of country.areaCodes) {
            if (nationalNumber.startsWith(code)) {
              areaCode = code;
              nationalNumber = nationalNumber.slice(code.length);
              break;
            }
          }
        }

        return {
          country: country.name,
          iso2: country.iso2,
          dialCode: country.dialCode,
          areaCode: areaCode ?? null,
          nationalNumber,
          formattedNumber: this.formatNumberWithMask(country.dialCode, nationalNumber, country.format, areaCode),
        };
      }
    }

    return {
      country: null,
      iso2: null,
      dialCode: null,
      areaCode: null,
      nationalNumber: digits,
      formattedNumber: digits ? `+${digits}` : '',
    };
  }

  public format(phone?: string | null) {
    return this.parse(phone).formattedNumber;
  }

  // ----------------------
  // Private helpers
  // ----------------------

  private emptyResult(): ParsedPhoneNumber {
    return {
      country: null,
      iso2: null,
      dialCode: null,
      areaCode: null,
      nationalNumber: '',
      formattedNumber: '',
    };
  }

  private formatNumberWithMask(
    dialCode?: string | null,
    nationalNumber?: string | null,
    mask?: string,
    areaCode?: string
  ): string {
    if (!dialCode || !nationalNumber) return '';

    if (areaCode) {
      return `+${dialCode} ${areaCode}-${nationalNumber}`;
    }

    if (!mask) {
      if (nationalNumber.length > 3) {
        return `+${dialCode} ${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3)}`;
      }
      return `+${dialCode} ${nationalNumber}`;
    }

    let formatted = '';
    let digitIndex = 0;

    for (const char of mask) {
      if (char === '.') {
        formatted += nationalNumber[digitIndex] ?? '';
        digitIndex++;
      } else {
        formatted += char;
      }
    }

    if (digitIndex < nationalNumber.length) {
      formatted += nationalNumber.slice(digitIndex);
    }

    return `+${dialCode} ${formatted}`;
  }
}
